import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Wrench, Trash2, RefreshCw, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { getBrokenLeaves, autoFixBrokenLeave, deleteLeave } from '../../services/api';

export const BrokenLeavesDialog = ({ open, onOpenChange, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBrokenLeaves();
      setRecords(res.data.records || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load broken records', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleFix = async (rec) => {
    setBusyId(rec.id);
    try {
      await autoFixBrokenLeave(rec.id);
      toast({ title: 'Repaired', description: `${rec.employee_name || rec.employee_id || 'Record'} fixed` });
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast({ title: 'Could not auto-fix', description: e.response?.data?.detail || 'Please delete or edit manually', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm(`Permanently delete this broken leave record for ${rec.employee_name || rec.employee_id || 'unknown employee'}? This cannot be undone.`)) return;
    setBusyId(rec.id);
    try {
      await deleteLeave(rec.id);
      toast({ title: 'Deleted', description: 'Broken record removed' });
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="broken-leaves-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-amber-600" /> Data Cleanup — Broken Leave Records
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-muted-foreground">
            Records the system skipped because they don't validate. Auto-fix backfills missing fields; delete removes bad entries.
          </p>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="broken-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground" data-testid="broken-loading">Scanning records…</div>
        ) : records.length === 0 ? (
          <div className="py-12 text-center" data-testid="broken-empty">
            <ShieldCheck className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
            <p className="font-medium">All clear</p>
            <p className="text-sm text-muted-foreground">No broken leave records found.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="broken-list">
            {records.map((rec) => (
              <div key={rec.id} className="border rounded-lg p-3" data-testid={`broken-row-${rec.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{rec.employee_name || <span className="italic text-muted-foreground">No name</span>}</span>
                      <span className="text-xs text-muted-foreground">{rec.employee_id}</span>
                      {rec.leave_type && <Badge variant="secondary" className="text-xs">{rec.leave_type}</Badge>}
                      {rec.fixable
                        ? <Badge className="text-xs bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Auto-fixable</Badge>
                        : <Badge className="text-xs bg-rose-100 text-rose-700 hover:bg-rose-100">Manual only</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {rec.start_date || '—'} → {rec.end_date || '—'} · {rec.days ?? '—'} days · {rec.year || 'no year'} · {rec.status || 'no status'}
                    </div>
                    <ul className="mt-2 space-y-1">
                      {rec.issues.map((iss, i) => (
                        <li key={i} className="text-xs text-rose-600 flex items-start gap-1">
                          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> <span className="break-all">{iss}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!rec.fixable || busyId === rec.id}
                      onClick={() => handleFix(rec)}
                      data-testid={`broken-fix-${rec.id}`}
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    >
                      <Wrench className="h-4 w-4 mr-1" /> Auto-fix
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyId === rec.id}
                      onClick={() => handleDelete(rec)}
                      data-testid={`broken-delete-${rec.id}`}
                      className="border-rose-200 text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BrokenLeavesDialog;
